import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from '../../../../i18n';
import { BONUS_INTRO_MS } from '../../../../constants/bonusConstants';
import { POINTS_PER_ANIMAL, COMBO_BONUS, COMBO_WINDOW_MS, SAVE_THE_ANIMALS_CONFIG } from './constants';

export interface SaveTheAnimalsPlayerProps {
  themeTitle: string;
  animals: string[];
  timeLimit: number;
  onSubmit: (answer: string, points?: number) => void;
  onScoreChange?: (score: number) => void;
  disabled?: boolean;
}

interface FallingAnimal {
  id: number;
  emoji: string;
  xPercent: number;
  yPercent: number;
  /** Per-animal fall speed multiplier (e.g. 0.75 = slower, 1.35 = faster). */
  speedMultiplier: number;
}

interface SavedAnimal {
  id: number;
  emoji: string;
  /** Fraction of container width from platform left edge (0 = left edge of platform, 1 = right edge). */
  offsetFromPlatformLeft: number;
  /** When the animal landed, for bounce animation. */
  landedAt: number;
}

const FALL_SPEED = SAVE_THE_ANIMALS_CONFIG.fallSpeedPerSecond;
const FALL_SPEED_MUL_MIN = SAVE_THE_ANIMALS_CONFIG.fallSpeedMultiplierMin;
const FALL_SPEED_MUL_MAX = SAVE_THE_ANIMALS_CONFIG.fallSpeedMultiplierMax;
const SPAWN_INTERVAL = SAVE_THE_ANIMALS_CONFIG.spawnIntervalMs;
const SPAWN_COUNT_MAX = SAVE_THE_ANIMALS_CONFIG.spawnCountMax;
const PLATFORM_WIDTH = SAVE_THE_ANIMALS_CONFIG.platformWidthFraction;
const CATCH_MARGIN = SAVE_THE_ANIMALS_CONFIG.catchMarginFraction;
const PLATFORM_STRIP_HEIGHT_PX = SAVE_THE_ANIMALS_CONFIG.platformStripHeightPx;
const FENCE_WIDTH_PX = SAVE_THE_ANIMALS_CONFIG.fenceWidthPx;
const PLATFORM_INSET_PX = 8;

function pickAnimal(animals: string[]): string {
  return animals[Math.floor(Math.random() * animals.length)] ?? '🐱';
}

function randomSpeedMultiplier(): number {
  return FALL_SPEED_MUL_MIN + Math.random() * (FALL_SPEED_MUL_MAX - FALL_SPEED_MUL_MIN);
}

export const SaveTheAnimalsPlayer: React.FC<SaveTheAnimalsPlayerProps> = ({
  themeTitle,
  animals,
  timeLimit,
  onSubmit,
  onScoreChange,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [savedCount, setSavedCount] = useState(0);
  const [totalPoints, setTotalPoints] = useState(0);
  const [falling, setFalling] = useState<FallingAnimal[]>([]);
  const [savedOnPlatform, setSavedOnPlatform] = useState<SavedAnimal[]>([]);
  const [platformLeft, setPlatformLeft] = useState(0.5 - PLATFORM_WIDTH / 2);
  const [submitted, setSubmitted] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartX = useRef(0);
  const dragStartLeft = useRef(0);
  const nextId = useRef(0);
  const lastSpawn = useRef(0);
  const lastTick = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const onSubmitRef = useRef(onSubmit);
  const scoreRef = useRef(0);
  const submitRef = useRef(false);
  const platformLeftRef = useRef(platformLeft);
  const lastCatchTimeRef = useRef<number>(0);
  const [gameStarted, setGameStarted] = useState(false);

  platformLeftRef.current = platformLeft;

  useEffect(() => {
    onSubmitRef.current = onSubmit;
  }, [onSubmit]);

  // Freeze the play field when the round ends. Keep the gathered score intact:
  // the parent uses it for the Time's Up overlay and any late submission.
  useEffect(() => {
    if (disabled) {
      setGameStarted(false);
      setFalling([]);
      setIsDragging(false);
    }
  }, [disabled]);

  // Start game after intro (like catch-the-train: setTimeout then set state so effects run)
  useEffect(() => {
    if (disabled || submitted || animals.length === 0) return;
    const t = setTimeout(() => {
      lastSpawn.current = Date.now();
      lastTick.current = Date.now();
      setGameStarted(true);
    }, BONUS_INTRO_MS);
    return () => clearTimeout(t);
  }, [disabled, submitted, animals.length]);

  // Spawn new falling animals (runs when gameStarted becomes true); 1–2 per tick, variable speeds
  useEffect(() => {
    if (!gameStarted || disabled || submitted || !animals.length) return;
    const interval = setInterval(() => {
      const now = Date.now();
      if (now - lastSpawn.current < SPAWN_INTERVAL) return;
      lastSpawn.current = now;
      const count = 1 + Math.floor(Math.random() * SPAWN_COUNT_MAX);
      const newOnes: FallingAnimal[] = [];
      for (let i = 0; i < count; i++) {
        newOnes.push({
          id: nextId.current++,
          emoji: pickAnimal(animals),
          xPercent: 10 + Math.random() * 80,
          yPercent: 0,
          speedMultiplier: randomSpeedMultiplier(),
        });
      }
      setFalling((prev) => [...prev, ...newOnes]);
    }, 250);
    return () => clearInterval(interval);
  }, [gameStarted, disabled, submitted, animals]);

  // Fall and collision (single RAF loop; read platform from ref so we don't restart on every drag)
  useEffect(() => {
    if (!gameStarted || disabled || submitted) return;
    let raf = 0;
    const tick = () => {
      const now = Date.now();
      const dt = Math.min((now - lastTick.current) / 1000, 0.1);
      lastTick.current = now;
      const pl = platformLeftRef.current;
      setFalling((prev) => {
        const next: FallingAnimal[] = [];
        const newlySaved: SavedAnimal[] = [];
        for (const a of prev) {
          const newY = a.yPercent + FALL_SPEED * a.speedMultiplier * dt * 100;
          if (newY >= 100) {
            const platformLeftPercent = pl * 100;
            const platformRightPercent = (pl + PLATFORM_WIDTH) * 100;
            const margin = CATCH_MARGIN * 100;
            if (a.xPercent >= platformLeftPercent - margin && a.xPercent <= platformRightPercent + margin) {
              const offsetFromPlatformLeft = (a.xPercent / 100 - pl) / PLATFORM_WIDTH;
              newlySaved.push({
                id: a.id,
                emoji: a.emoji,
                offsetFromPlatformLeft: Math.max(0, Math.min(1, offsetFromPlatformLeft)),
                landedAt: Date.now(),
              });
            }
            continue;
          }
          next.push({ ...a, yPercent: newY });
        }
        if (newlySaved.length > 0) {
          let pointsToAdd = 0;
          const now = Date.now();
          for (let i = 0; i < newlySaved.length; i++) {
            const isCombo =
              lastCatchTimeRef.current > 0 &&
              now - lastCatchTimeRef.current < COMBO_WINDOW_MS;
            pointsToAdd += POINTS_PER_ANIMAL + (isCombo ? COMBO_BONUS : 0);
            lastCatchTimeRef.current = now;
          }
          setTotalPoints((p) => {
            const nextPoints = p + pointsToAdd;
            // Update synchronously so a simultaneous bonus-time-up submits the
            // same score the player can already see on screen.
            scoreRef.current = nextPoints;
            onScoreChange?.(nextPoints);
            return nextPoints;
          });
          setSavedCount((c) => c + newlySaved.length);
          setSavedOnPlatform((s) => [...s, ...newlySaved]);
        }
        return next;
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [gameStarted, disabled, submitted, onScoreChange]);

  const handlePointerDown = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (disabled || submitted) return;
      const clientX = 'touches' in e ? e.touches[0]?.clientX : e.clientX;
      if (clientX == null) return;
      setIsDragging(true);
      dragStartX.current = clientX;
      dragStartLeft.current = platformLeft;
    },
    [disabled, submitted, platformLeft]
  );

  const handlePointerMove = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!isDragging) return;
      const clientX = 'touches' in e ? (e as TouchEvent).touches[0]?.clientX : (e as MouseEvent).clientX;
      const container = containerRef.current;
      if (!container || clientX == null) return;
      const rect = container.getBoundingClientRect();
      const deltaX = (clientX - dragStartX.current) / rect.width;
      let next = dragStartLeft.current + deltaX;
      next = Math.max(0, Math.min(1 - PLATFORM_WIDTH, next));
      setPlatformLeft(next);
    },
    [isDragging]
  );

  const handlePointerUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      dragStartX.current = 0;
      dragStartLeft.current = platformLeft;
    }
  }, [isDragging, platformLeft]);

  useEffect(() => {
    window.addEventListener('mousemove', handlePointerMove);
    window.addEventListener('mouseup', handlePointerUp);
    window.addEventListener('touchmove', handlePointerMove, { passive: true });
    window.addEventListener('touchend', handlePointerUp);
    return () => {
      window.removeEventListener('mousemove', handlePointerMove);
      window.removeEventListener('mouseup', handlePointerUp);
      window.removeEventListener('touchmove', handlePointerMove);
      window.removeEventListener('touchend', handlePointerUp);
    };
  }, [handlePointerMove, handlePointerUp]);

  // Submit on time-up
  useEffect(() => {
    if (submitRef.current || disabled) return;
    const timer = setTimeout(() => {
      if (!submitRef.current) {
        submitRef.current = true;
        setSubmitted(true);
        onSubmitRef.current('time-up', scoreRef.current);
      }
    }, timeLimit);
    return () => clearTimeout(timer);
  }, [timeLimit, disabled]);

  useEffect(() => {
    if (disabled && !submitRef.current) {
      submitRef.current = true;
      setSubmitted(true);
      onSubmitRef.current('time-up', scoreRef.current);
    }
  }, [disabled]);

  const platformHeightPx = SAVE_THE_ANIMALS_CONFIG.platformHeightPx;
  const platformStyle: React.CSSProperties = {
    position: 'absolute',
    bottom: PLATFORM_INSET_PX,
    left: `${platformLeft * 100}%`,
    width: `${PLATFORM_WIDTH * 100}%`,
    height: platformHeightPx,
    maxWidth: '100%',
    boxSizing: 'border-box',
    backgroundColor: 'rgba(52, 211, 153, 0.9)',
    borderRadius: 14,
    border: '3px solid rgba(52, 211, 153, 1)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
    cursor: disabled ? 'default' : 'grab',
    userSelect: 'none',
    transition: isDragging ? 'none' : 'left 0.05s ease-out',
    pointerEvents: disabled ? 'none' : 'auto',
    zIndex: 10,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
  };

  // Fences: not too high, ~1/5 of typical platform width (~20px)
  const fenceHeightPx = 20;
  const fenceStyle: React.CSSProperties = {
    width: FENCE_WIDTH_PX,
    height: fenceHeightPx,
    backgroundColor: 'rgba(52, 211, 153, 1)',
    border: '2px solid rgba(34, 160, 115, 1)',
    borderRadius: 2,
    flexShrink: 0,
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 0',
        minHeight: 0,
        overflow: 'hidden',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          flexShrink: 0,
          fontSize: 'clamp(1rem, 2.5vw, 1.35rem)',
          fontWeight: 700,
          color: 'var(--text-primary)',
          padding: '0.7rem 1rem 0.4rem',
        }}
      >
        {themeTitle}
      </div>

      <div
        style={{
          flexShrink: 0,
          display: 'flex',
          justifyContent: 'center',
          gap: '0.75rem',
          marginBottom: '0.75rem',
          flexWrap: 'wrap',
          padding: '0 1rem',
        }}
      >
        <span
          style={{
            fontSize: '1.1rem',
            fontWeight: 700,
            color: '#047857',
            background: 'rgba(52, 211, 153, 0.16)',
            border: '1.5px solid rgba(52, 211, 153, 0.35)',
            borderRadius: 999,
            padding: '0.45rem 0.85rem',
            boxShadow: 'var(--shadow-sm)',
          }}
        >
          {t('bonus.player.savedLabel', { count: savedCount })}
        </span>
        <span
          style={{
            fontSize: '1.1rem',
            fontWeight: 700,
            color: 'var(--text-primary)',
            background: 'rgba(255, 255, 255, 0.9)',
            border: '1.5px solid rgba(var(--color-primary-rgb), 0.18)',
            borderRadius: 999,
            padding: '0.45rem 0.85rem',
            boxShadow: 'var(--shadow-sm)',
          }}
        >
          Points: {totalPoints}
        </span>
      </div>

      {/* Play area: falling zone + fixed platform strip at bottom */}
      <div
        ref={containerRef}
        style={{
          flex: '1 1 auto',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          overflow: 'hidden',
          opacity: disabled ? 0.6 : 1,
          transition: 'opacity 0.3s',
          touchAction: 'none',
        }}
      >
        {/* Falling zone: animals fall here (0–100% of this block) */}
        <div
          style={{
            flex: '1 1 auto',
            minHeight: 0,
            position: 'relative',
            width: '100%',
            overflow: 'hidden',
          }}
        >
          {falling.map((a) => (
            <div
              key={a.id}
              style={{
                position: 'absolute',
                left: `${a.xPercent}%`,
                top: `${a.yPercent}%`,
                transform: 'translate(-50%, -50%)',
                fontSize: SAVE_THE_ANIMALS_CONFIG.emojiSizePx,
                lineHeight: 1,
                pointerEvents: 'none',
                zIndex: 5,
                transition: 'none',
              }}
            >
              {a.emoji}
            </div>
          ))}
        </div>

        {/* Platform strip: fixed height, platform and saved animals sit here */}
        <div
          style={{
            flexShrink: 0,
            height: PLATFORM_STRIP_HEIGHT_PX,
            position: 'relative',
            width: '100%',
          }}
        >
          {savedOnPlatform.map((a) => {
            const leftPercent = (platformLeft + a.offsetFromPlatformLeft * PLATFORM_WIDTH) * 100;
            return (
              <div
                key={a.id}
                className="save-the-animals__saved--bounce"
                style={{
                  position: 'absolute',
                  left: `${leftPercent}%`,
                  bottom: platformHeightPx + PLATFORM_INSET_PX + 4,
                  transform: 'translateX(-50%)',
                  fontSize: SAVE_THE_ANIMALS_CONFIG.emojiSizePx * 0.85,
                  lineHeight: 1,
                  pointerEvents: 'none',
                  zIndex: 6,
                }}
              >
                {a.emoji}
              </div>
            );
          })}
          <div
            role="slider"
            aria-label={t('bonus.player.catchPlatformAria')}
            style={platformStyle}
            onMouseDown={handlePointerDown}
            onTouchStart={handlePointerDown}
          >
            <div style={fenceStyle} />
            <div style={{ ...fenceStyle, order: 1 }} />
          </div>
        </div>
      </div>
    </div>
  );
};
